import { useId, useState, type Ref } from "react";

import type { WatchSignalDefinition } from "../../lib/watchSignalCatalog";
import styles from "./WatchSignalPicker.module.css";

interface SelectedSignalIdentity {
  signalId: string;
  definitionVersion: number;
}

export interface WatchCatalogSignalDisclosureProps {
  title: string;
  description: string;
  legend: string;
  signals: readonly WatchSignalDefinition[];
  selected: SelectedSignalIdentity | null;
  onSelect: (signal: WatchSignalDefinition) => void;
  onExpandedChange?: (expanded: boolean) => void;
  invalid?: boolean;
  errorMessageId?: string;
  choicesRef?: Ref<HTMLFieldSetElement>;
  radioName: string;
}

/**
 * Progressive disclosure for one server-advertised signal family.
 *
 * The component is deliberately presentation-only: it renders reviewed
 * catalog copy and returns the exact selected definition without interpreting
 * thresholds, formulas, evidence, or provider facts in the browser.
 */
export default function WatchCatalogSignalDisclosure({
  title,
  description,
  legend,
  signals,
  selected,
  onSelect,
  onExpandedChange,
  invalid = false,
  errorMessageId,
  choicesRef,
  radioName,
}: WatchCatalogSignalDisclosureProps) {
  const choicesId = useId();
  const [expanded, setExpanded] = useState<boolean | null>(null);
  const selectedSignal = signals.find((signal) => (
    signal.signal_id === selected?.signalId
    && signal.definition_version === selected.definitionVersion
  ));
  const showChoices = expanded ?? Boolean(selectedSignal);

  return (
    <div className={styles.signalGroup}>
      <button
        type="button"
        className={styles.disclosure}
        data-selected={Boolean(selectedSignal) || undefined}
        aria-expanded={showChoices}
        aria-controls={choicesId}
        aria-describedby={selectedSignal ? `${choicesId}-selection` : undefined}
        aria-label={title}
        onClick={() => {
          const nextExpanded = !showChoices;
          setExpanded(nextExpanded);
          onExpandedChange?.(nextExpanded);
        }}
      >
        <span className={styles.signalCopy}>
          <strong>{title}</strong>
          <span>{description}</span>
        </span>
        {selectedSignal ? <span id={`${choicesId}-selection`} className={styles.selectionBadge}>Selected</span> : null}
        <span className={styles.chevron} aria-hidden="true">{showChoices ? "−" : "+"}</span>
      </button>
      {showChoices ? (
        <fieldset
          id={choicesId}
          ref={choicesRef}
          className={styles.referenceChoices}
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? errorMessageId : undefined}
        >
          <legend>{legend}</legend>
          {signals.map((signal) => {
            const isSelected = signal.signal_id === selected?.signalId
              && signal.definition_version === selected.definitionVersion;
            return (
              <label
                className={styles.referenceChoice}
                key={`${signal.signal_id}:${signal.definition_version}`}
              >
                <input
                  type="radio"
                  name={radioName}
                  checked={isSelected}
                  onChange={() => { if (!isSelected) onSelect(signal); }}
                />
                <span>
                  <strong>{signal.customer.name}</strong>
                  <small>{signal.customer.description}</small>
                </span>
              </label>
            );
          })}
        </fieldset>
      ) : null}
    </div>
  );
}
